import type { AssistantConversationListItem } from "../../types";
import { CONVERSATION_STATUS_LABELS } from "./conversations-filters";

/**
 * The reason line under a conversation title, and the tone it is drawn in.
 *
 * `accent` is reserved for the lines that mean "this one is waiting for you" — it is the text
 * half of the blue dot, so it must not leak onto a working or idle row.
 */
export type ConversationDescription = {
  text: string;
  tone: "accent" | "muted";
} | null;

/**
 * Pure: the reason line for one conversation row. Every surface (flyout, panel tab, list
 * screen) reads it from here so the three never disagree about what a status means.
 *
 * Returns `null` when there is nothing honest to say — an idle conversation whose projection
 * cannot name who spoke last. The row then renders its title alone rather than inventing a
 * sentence; see `AssistantConversationListItem.lastMessageAuthor`.
 *
 * The working line is plain "Working" by decision D4: the activity phrase from the Pencil
 * design ("Working — analysing losses") lives in `assistantRunProgress`, which no list query
 * reads.
 */
export function describeConversation(
  item: Pick<AssistantConversationListItem, "status" | "reason" | "lastMessageAuthor">
): ConversationDescription {
  if (item.status === "working") {
    // The same word the STATUS column and the Status filter use — one vocabulary, one source.
    return { text: CONVERSATION_STATUS_LABELS.working, tone: "muted" };
  }

  if (item.status === "needs_you") {
    switch (item.reason) {
      case "awaiting_answer":
        return { text: "Waiting for your answer", tone: "accent" };
      case "awaiting_approval":
        return { text: "Waiting for your approval", tone: "accent" };
      case "finished_unread":
        return { text: "Finished — unread reply", tone: "accent" };
      default:
        // Needs-you with no reason still deserves the accent line: the dot is already up and
        // an unexplained dot is worse than a general one.
        return { text: "Waiting for you", tone: "accent" };
    }
  }

  if (item.lastMessageAuthor === "user") {
    return { text: "You · last message", tone: "muted" };
  }
  if (item.lastMessageAuthor === "assistant") {
    return { text: "Claudia · last message", tone: "muted" };
  }

  return null;
}

/**
 * The accessible name for a conversation row: the title, then the reason it is in its state.
 *
 * A screen reader gets no spinner and no dot, so without this a needs-you row and an idle row
 * announce identically.
 */
export function describeConversationForScreenReader(
  item: Pick<AssistantConversationListItem, "title" | "status" | "reason" | "lastMessageAuthor">
): string {
  const title = item.title.trim().length > 0 ? item.title.trim() : "Untitled conversation";
  const description = describeConversation(item);
  return description ? `${title}, ${description.text}` : title;
}
